import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../redux/hooks'
import { 
  fetchUserById, 
  fetchUserActivityLogs,
  fetchUserDevices,
  fetchUserOrganizations,
  suspendUser,
  unsuspendUser,
  resetUserPassword 
} from '../redux/reducers/usersSlice'
import { CONFIG } from '../../config'
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  Clock,
  MapPin,
  Globe,
  Shield,
  Activity,
  Smartphone,
  Tablet,
  Laptop,
  CheckCircle,
  XCircle,
  Ban,
  Key,
  Send,
  Download,
  Trash2,
  AlertTriangle,
  Bell,
  Copy,
  Building2,
  Crown,
  Monitor,
  Wifi,
  Chrome,
  Hash,
  LogIn,
  Settings,
  CreditCard,
  ShoppingCart,
  MessageSquare,
  FileText,
  Eye,
  Edit,
  UserCheck,
  UserX,
  MoreVertical,
} from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'

const UserDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { selectedUser, activityLogs, devices, userOrganizations, loading } = useAppSelector((state) => state.users)
  const [activeTab, setActiveTab] = useState<'overview' | 'activity' | 'sessions' | 'permissions' | 'devices' | 'organizations'>('overview')
  const [showConfirmDialog, setShowConfirmDialog] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const [testingPush, setTestingPush] = useState<string | null>(null)
  const [pushTestResult, setPushTestResult] = useState<{ deviceId: string; success: boolean; message: string } | null>(null)
  
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserById(userId))
      dispatch(fetchUserActivityLogs(userId))
      dispatch(fetchUserDevices(userId))
      dispatch(fetchUserOrganizations(userId))
    }
  }, [dispatch, userId])

  const handleSuspendUser = async () => {
    if (!selectedUser) return
    setActionLoading(true)
    try {
      await dispatch(suspendUser({ userId: selectedUser.uuid, reason: 'Admin action' })).unwrap()
      setShowConfirmDialog(null)
      // Refresh user data
      dispatch(fetchUserById(userId!))
    } catch (error) {
      console.error('Failed to suspend user:', error)
    }
    setActionLoading(false)
  }

  const handleUnsuspendUser = async () => {
    if (!selectedUser) return
    setActionLoading(true)
    try {
      await dispatch(unsuspendUser(selectedUser.uuid)).unwrap()
      setShowConfirmDialog(null)
      // Refresh user data
      dispatch(fetchUserById(userId!))
    } catch (error) {
      console.error('Failed to unsuspend user:', error)
    }
    setActionLoading(false)
  }

  const handleResetPassword = async () => {
    if (!selectedUser) return
    setActionLoading(true)
    try {
      await dispatch(resetUserPassword({ userId: selectedUser.uuid, sendEmail: true })).unwrap()
      setShowConfirmDialog(null)
    } catch (error) {
      console.error('Failed to reset password:', error)
    }
    setActionLoading(false)
  }

  const handleTestPushNotification = async (deviceId: string, pushToken: string) => {
    setTestingPush(deviceId)
    setPushTestResult(null)
    
    try {
      const response = await fetch(`${CONFIG.service.baseUrl}/test-push-notification?token=${encodeURIComponent(pushToken)}&title=${encodeURIComponent('Test Notification')}&body=${encodeURIComponent(`Test from Backoffice for ${selectedUser?.name || 'User'}`)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_access_token')}`
        }
      })
      
      const result = await response.json()
      
      if (response.ok) {
        setPushTestResult({ 
          deviceId, 
          success: true, 
          message: 'Push notification sent successfully!' 
        })
      } else {
        setPushTestResult({ 
          deviceId, 
          success: false, 
          message: result.message || 'Failed to send push notification' 
        })
      }
    } catch (error) {
      setPushTestResult({ 
        deviceId, 
        success: false, 
        message: 'Network error: Could not send push notification' 
      })
    } finally {
      setTestingPush(null)
      // Clear result after 3 seconds
      setTimeout(() => setPushTestResult(null), 3000)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    // Could add a toast notification here
  }
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle className="w-3 h-3 mr-1" />
            Active
          </span>
        )
      case 'inactive':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400">
            <XCircle className="w-3 h-3 mr-1" />
            Inactive
          </span>
        )
      case 'suspended':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
            <Ban className="w-3 h-3 mr-1" />
            Suspended
          </span>
        )
      default:
        return null
    }
  }

  const getActivityIcon = (action: string) => {
    const actionLower = action.toLowerCase()
    if (actionLower.includes('login')) return <LogIn className="w-4 h-4" />
    if (actionLower.includes('password')) return <Key className="w-4 h-4" />
    if (actionLower.includes('profile')) return <User className="w-4 h-4" />
    if (actionLower.includes('settings')) return <Settings className="w-4 h-4" />
    if (actionLower.includes('payment')) return <CreditCard className="w-4 h-4" />
    if (actionLower.includes('order') || actionLower.includes('purchase')) return <ShoppingCart className="w-4 h-4" />
    if (actionLower.includes('message') || actionLower.includes('comment')) return <MessageSquare className="w-4 h-4" />
    if (actionLower.includes('document') || actionLower.includes('file')) return <FileText className="w-4 h-4" />
    if (actionLower.includes('view')) return <Eye className="w-4 h-4" />
    if (actionLower.includes('edit') || actionLower.includes('update')) return <Edit className="w-4 h-4" />
    return <Activity className="w-4 h-4" />
  }

  const getActivityColor = (action: string) => {
    const actionLower = action.toLowerCase()
    if (actionLower.includes('login')) return 'text-blue-500'
    if (actionLower.includes('error') || actionLower.includes('fail')) return 'text-red-500'
    if (actionLower.includes('success')) return 'text-green-500'
    if (actionLower.includes('warning')) return 'text-yellow-500'
    return 'text-gray-500'
  }

  if (loading && !selectedUser) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!selectedUser) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <UserX className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-lg font-medium text-muted-foreground">User not found</p>
          <button
            onClick={() => navigate('/users')}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Back to Users
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Page Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/users')}
              className="p-2 hover:bg-muted rounded-md transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-lg font-bold text-white">
                    {selectedUser.name?.substring(0, 2).toUpperCase() || 'U'}
                  </span>
                </div>
                {selectedUser.status === 'active' && (
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-card rounded-full"></div>
                )}
              </div>
              
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold text-foreground">{selectedUser.name || 'Unknown User'}</h1>
                  {getStatusBadge(selectedUser.status)}
                </div>
                <div className="flex items-center space-x-4 mt-1">
                  <p className="text-sm text-muted-foreground flex items-center">
                    <Mail className="w-3 h-3 mr-1" />
                    {selectedUser.email}
                  </p>
                  <p className="text-sm text-muted-foreground flex items-center">
                    <Hash className="w-3 h-3 mr-1" />
                    {selectedUser.uuid}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center space-x-2">
            {selectedUser.status === 'suspended' ? (
              <button
                onClick={() => setShowConfirmDialog('unsuspend')}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center text-sm"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Unsuspend User
              </button>
            ) : (
              <button
                onClick={() => setShowConfirmDialog('suspend')}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center text-sm"
              >
                <UserX className="w-4 h-4 mr-2" />
                Suspend User
              </button>
            )}
            
            <div className="relative">
              <button
                onClick={() => setShowActionsMenu(!showActionsMenu)}
                className="p-2 hover:bg-muted rounded-md transition-colors"
              >
                <MoreVertical className="w-5 h-5 text-muted-foreground" />
              </button>
              
              {showActionsMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-md shadow-lg z-10">
                  <button
                    onClick={() => {
                      setShowConfirmDialog('reset')
                      setShowActionsMenu(false)
                    }}
                    className="w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors flex items-center"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Reset Password
                  </button>
                  <button
                    onClick={() => setShowActionsMenu(false)}
                    className="w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors flex items-center"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </button>
                  <button
                    onClick={() => setShowActionsMenu(false)}
                    className="w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Data
                  </button>
                  <hr className="my-1 border-border" />
                  <button
                    onClick={() => setShowActionsMenu(false)}
                    className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete User
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-border bg-muted/30">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'overview' 
              ? 'text-foreground border-b-2 border-primary bg-card' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <User className="w-4 h-4" />
          <span>Overview</span>
        </button>
        <button
          onClick={() => setActiveTab('organizations')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'organizations' 
              ? 'text-foreground border-b-2 border-primary bg-card' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <Building2 className="w-4 h-4" />
          <span>Organizations</span>
        </button>
        <button
          onClick={() => setActiveTab('devices')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'devices' 
              ? 'text-foreground border-b-2 border-primary bg-card' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <Smartphone className="w-4 h-4" />
          <span>Devices</span>
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'activity' 
              ? 'text-foreground border-b-2 border-primary bg-card' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <Activity className="w-4 h-4" />
          <span>Activity Log</span>
        </button>
        <button
          onClick={() => setActiveTab('sessions')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'sessions' 
              ? 'text-foreground border-b-2 border-primary bg-card' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <Monitor className="w-4 h-4" />
          <span>Sessions</span>
        </button>
        <button
          onClick={() => setActiveTab('permissions')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'permissions' 
              ? 'text-foreground border-b-2 border-primary bg-card' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <Shield className="w-4 h-4" />
          <span>Permissions</span>
        </button>
      </div>
      
      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'overview' ? (
          <div className="space-y-6 max-w-7xl">
            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-semibold">Total Logins</p>
                    <p className="text-2xl font-bold text-foreground mt-1">{selectedUser.loginCount || 0}</p>
                  </div>
                  <LogIn className="w-8 h-8 text-muted-foreground/30" />
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-semibold">Account Age</p>
                    <p className="text-2xl font-bold text-foreground mt-1">
                      {selectedUser.registeredAt 
                        ? Math.floor((Date.now() - new Date(selectedUser.registeredAt).getTime()) / (1000 * 60 * 60 * 24))
                        : 0}d
                    </p>
                  </div>
                  <Calendar className="w-8 h-8 text-muted-foreground/30" />
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-semibold">Email Status</p>
                    <p className="text-sm font-semibold mt-1">
                      {selectedUser.emailVerified ? (
                        <span className="text-green-500 flex items-center">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Verified
                        </span>
                      ) : (
                        <span className="text-yellow-500 flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          Unverified
                        </span>
                      )}
                    </p>
                  </div>
                  <Mail className="w-8 h-8 text-muted-foreground/30" />
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-semibold">Last Activity</p>
                    <p className="text-sm font-semibold text-foreground mt-1">
                      {selectedUser.lastLoginAt 
                        ? formatDistanceToNow(new Date(selectedUser.lastLoginAt), { addSuffix: true })
                        : 'Never'}
                    </p>
                  </div>
                  <Clock className="w-8 h-8 text-muted-foreground/30" />
                </div>
              </div>
            </div>
            
            {/* Personal Information */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <User className="w-5 h-5 mr-2" />
                Personal Information
              </h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Full Name</p>
                  <p className="text-sm font-medium text-foreground">{selectedUser.name || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Email Address</p>
                  <p className="text-sm font-medium text-foreground">{selectedUser.email}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Phone Number</p>
                  <p className="text-sm font-medium text-foreground">
                    {selectedUser.phoneNumber || 'Not provided'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Location</p>
                  <p className="text-sm font-medium text-foreground">
                    {selectedUser.city && selectedUser.country 
                      ? `${selectedUser.city}, ${selectedUser.country}`
                      : selectedUser.country || 'Not provided'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Timezone</p>
                  <p className="text-sm font-medium text-foreground">
                    {selectedUser.timezone || 'Not set'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Language</p>
                  <p className="text-sm font-medium text-foreground">
                    {selectedUser.preferredLanguage || 'English'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Account Information */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                Account Information
              </h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">User ID</p>
                  <p className="text-sm font-mono text-foreground">{selectedUser.uuid}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Account Status</p>
                  <div className="mt-1">{getStatusBadge(selectedUser.status)}</div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Registration Date</p>
                  <p className="text-sm font-medium text-foreground">
                    {selectedUser.registeredAt ? format(new Date(selectedUser.registeredAt), 'PPP') : 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Last Login</p>
                  <p className="text-sm font-medium text-foreground">
                    {selectedUser.lastLoginAt 
                      ? format(new Date(selectedUser.lastLoginAt), 'PPP HH:mm')
                      : 'Never logged in'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Email Verification</p>
                  <div className="flex items-center space-x-2 mt-1">
                    {selectedUser.emailVerified ? (
                      <span className="text-green-500 flex items-center text-sm font-medium">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Verified
                      </span>
                    ) : (
                      <span className="text-yellow-500 flex items-center text-sm font-medium">
                        <XCircle className="w-4 h-4 mr-1" />
                        Unverified
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Last Updated</p>
                  <p className="text-sm font-medium text-foreground">
                    {selectedUser.updatedAt ? format(new Date(selectedUser.updatedAt), 'PPP p') : 'Unknown'}
                  </p>
                </div>
              </div>
            </div>

            {/* Device Information */}
            {selectedUser.deviceInfo && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Smartphone className="w-5 h-5 mr-2" />
                  Device Information
                </h3>
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase mb-1">Platform</p>
                    <p className="text-sm font-medium text-foreground">
                      {selectedUser.deviceInfo.platform || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase mb-1">Device Type</p>
                    <p className="text-sm font-medium text-foreground">
                      {selectedUser.deviceInfo.deviceType || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase mb-1">App Version</p>
                    <p className="text-sm font-medium text-foreground">
                      {selectedUser.deviceInfo.lastUsedVersion || 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'activity' ? (
          <div className="max-w-4xl mx-auto">
            {activityLogs.length > 0 ? (
              <div className="space-y-3">
                {activityLogs.map((log) => (
                  <div key={log.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg bg-muted ${getActivityColor(log.action)}`}>
                        {getActivityIcon(log.action)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-foreground">{log.action}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                          </p>
                        </div>
                        {log.details && (
                          <div className="mt-2 p-2 bg-muted rounded text-xs font-mono text-muted-foreground">
                            {typeof log.details === 'object' 
                              ? JSON.stringify(log.details, null, 2)
                              : log.details}
                          </div>
                        )}
                        <div className="flex items-center space-x-4 mt-2">
                          {log.ipAddress && (
                            <p className="text-xs text-muted-foreground flex items-center">
                              <Wifi className="w-3 h-3 mr-1" />
                              {log.ipAddress}
                            </p>
                          )}
                          {log.userAgent && (
                            <p className="text-xs text-muted-foreground flex items-center">
                              <Chrome className="w-3 h-3 mr-1" />
                              {log.userAgent.substring(0, 50)}...
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(log.timestamp), 'PPP HH:mm')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Activity className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No activity logs available</p>
                <p className="text-sm text-muted-foreground mt-1">User activity will appear here once tracked</p>
              </div>
            )}
          </div>
        ) : activeTab === 'sessions' ? (
          <div className="text-center py-12">
            <Monitor className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-lg font-medium text-muted-foreground">No active sessions</p>
            <p className="text-sm text-muted-foreground mt-1">Active login sessions will appear here</p>
          </div>
        ) : activeTab === 'permissions' ? (
          <div className="text-center py-12">
            <Shield className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-lg font-medium text-muted-foreground">No special permissions</p>
            <p className="text-sm text-muted-foreground mt-1">This user has standard access rights</p>
          </div>
        ) : activeTab === 'devices' ? (
          <div className="space-y-4">
            {devices && devices.length > 0 ? (
              <div className="grid gap-4">
                {devices.map((device) => {
                  const getDeviceIcon = () => {
                    if (device.deviceType === 'mobile') return <Smartphone className="w-5 h-5" />
                    if (device.deviceType === 'tablet') return <Tablet className="w-5 h-5" />
                    return <Laptop className="w-5 h-5" />
                  }
                  
                  const getRelativeTime = (date: string) => {
                    const now = new Date()
                    const then = new Date(date)
                    const diffMs = now.getTime() - then.getTime()
                    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
                    
                    if (diffDays === 0) return 'Today'
                    if (diffDays === 1) return 'Yesterday'
                    if (diffDays < 7) return `${diffDays} days ago`
                    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
                    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
                    return `${Math.floor(diffDays / 365)} years ago`
                  }
                  
                  return (
                    <div key={device.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="p-2 bg-primary/10 rounded-lg text-primary">
                            {getDeviceIcon()}
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-foreground">
                              {device.deviceName || `${device.platform} Device`}
                            </h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              {device.platform} • Version {device.lastUsedVersion}
                            </p>
                            <div className="grid grid-cols-2 gap-4 mt-3">
                              <div>
                                <p className="text-xs text-muted-foreground">Last Active</p>
                                <p className="text-sm font-medium text-foreground">
                                  {getRelativeTime(device.lastActiveAt)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">First Seen</p>
                                <p className="text-sm font-medium text-foreground">
                                  {format(new Date(device.firstSeenAt), 'MMM d, yyyy')}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Login Count</p>
                                <p className="text-sm font-medium text-foreground">
                                  {device.loginCount} times
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">IP Address</p>
                                <p className="text-sm font-medium text-foreground">
                                  {device.ipAddress || 'Unknown'}
                                </p>
                              </div>
                            </div>
                            {device.pushToken && (
                              <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-xs text-muted-foreground">Push Token</p>
                                  <button 
                                    onClick={() => copyToClipboard(device.pushToken!)}
                                    className="text-muted-foreground hover:text-foreground p-1 hover:bg-muted rounded transition-colors"
                                    title="Copy token"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </button>
                                </div>
                                <p className="text-xs font-mono text-muted-foreground break-all">
                                  {device.pushToken.substring(0, 30)}...
                                </p>
                              </div>
                            )}
                            {device.userAgent && (
                              <div className="mt-3">
                                <p className="text-xs text-muted-foreground">User Agent</p>
                                <p className="text-xs font-mono text-muted-foreground mt-1 break-all">
                                  {device.userAgent}
                                </p>
                              </div>
                            )}
                            {pushTestResult?.deviceId === device.id && (
                              <div className={`mt-3 p-2 rounded-lg text-xs ${
                                pushTestResult.success 
                                  ? 'bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400' 
                                  : 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400'
                              }`}>
                                {pushTestResult.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          {device.pushToken && (
                            <button 
                              onClick={() => handleTestPushNotification(device.id, device.pushToken!)}
                              disabled={testingPush === device.id}
                              className={`p-2 rounded-lg transition-colors ${
                                testingPush === device.id 
                                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed' 
                                  : 'text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950'
                              }`}
                              title="Test push notification"
                            >
                              {testingPush === device.id ? (
                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Bell className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          <button className="text-red-500 hover:text-red-600 p-2 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Smartphone className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No devices found</p>
                <p className="text-sm text-muted-foreground mt-1">User devices will appear here when they log in</p>
              </div>
            )}
          </div>
        ) : activeTab === 'organizations' ? (
          <div className="space-y-4">
            {userOrganizations && userOrganizations.length > 0 ? (
              <div className="grid gap-4">
                {/* Organization stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-card border border-border rounded-lg p-4">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Total Organizations</p>
                    <p className="text-2xl font-bold text-foreground">{userOrganizations.length}</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Owned Organizations</p>
                    <p className="text-2xl font-bold text-foreground">
                      {userOrganizations.filter(org => org.isOwner).length}
                    </p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Active Organizations</p>
                    <p className="text-2xl font-bold text-foreground">
                      {userOrganizations.filter(org => org.organization.isActive).length}
                    </p>
                  </div>
                </div>
                
                {/* Organization cards */}
                {userOrganizations.map((userOrg) => {
                  const getRoleBadgeColor = () => {
                    if (userOrg.isOwner) return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                    if (userOrg.role.name === 'Admin') return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                    return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                  }
                  
                  const getProviderBadge = () => {
                    if (!userOrg.provider || userOrg.provider === 'internal') return null
                    const providerLabels: Record<string, string> = {
                      'azure-ad': 'Azure AD',
                      'google': 'Google',
                      'okta': 'Okta',
                      'generic-saml': 'SAML',
                      'generic-oidc': 'OIDC'
                    }
                    return providerLabels[userOrg.provider] || userOrg.provider
                  }
                  
                  return (
                    <div 
                      key={userOrg.organization.id} 
                      className={`bg-card border rounded-lg p-6 hover:shadow-md transition-shadow ${
                        userOrg.isOwner ? 'border-yellow-500 dark:border-yellow-600' : 'border-border'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="text-lg font-semibold text-foreground flex items-center">
                              {userOrg.isOwner && (
                                <Crown className="w-5 h-5 text-yellow-500 mr-2" />
                              )}
                              {userOrg.organization.name}
                            </h4>
                            {!userOrg.organization.isActive && (
                              <span className="px-2 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs rounded-full">
                                Inactive
                              </span>
                            )}
                          </div>
                          
                          {userOrg.organization.description && (
                            <p className="text-sm text-muted-foreground mb-3">
                              {userOrg.organization.description}
                            </p>
                          )}
                          
                          <div className="flex flex-wrap gap-2 mb-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor()}`}>
                              {userOrg.isOwner ? 'Owner' : userOrg.role.name}
                            </span>
                            {userOrg.branch && (
                              <span className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-xs">
                                {userOrg.branch.name}
                              </span>
                            )}
                            {getProviderBadge() && (
                              <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-xs">
                                {getProviderBadge()}
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground">Joined</p>
                              <p className="text-sm font-medium text-foreground">
                                {format(new Date(userOrg.joinedAt), 'MMM d, yyyy')}
                              </p>
                            </div>
                            {userOrg.organization.email && (
                              <div>
                                <p className="text-xs text-muted-foreground">Contact Email</p>
                                <p className="text-sm font-medium text-foreground">
                                  {userOrg.organization.email}
                                </p>
                              </div>
                            )}
                            {userOrg.organization.phone && (
                              <div>
                                <p className="text-xs text-muted-foreground">Phone</p>
                                <p className="text-sm font-medium text-foreground">
                                  {userOrg.organization.phone}
                                </p>
                              </div>
                            )}
                            {userOrg.externalId && (
                              <div>
                                <p className="text-xs text-muted-foreground">External ID</p>
                                <p className="text-sm font-mono text-muted-foreground">
                                  {userOrg.externalId}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex space-x-2 ml-4">
                          <button className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors" title="View organization">
                            <Eye className="w-4 h-4" />
                          </button>
                          {!userOrg.isOwner && (
                            <button className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition-colors" title="Remove from organization">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No organizations found</p>
                <p className="text-sm text-muted-foreground mt-1">This user is not part of any organization</p>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Confirmation Dialogs */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowConfirmDialog(null)}>
          <div className="bg-white dark:bg-gray-800 border border-border rounded-lg p-6 max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {showConfirmDialog === 'suspend' && 'Suspend User?'}
              {showConfirmDialog === 'unsuspend' && 'Unsuspend User?'}
              {showConfirmDialog === 'reset' && 'Reset Password?'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {showConfirmDialog === 'suspend' && `Are you sure you want to suspend ${selectedUser.name}? They will not be able to access their account.`}
              {showConfirmDialog === 'unsuspend' && `Are you sure you want to unsuspend ${selectedUser.name}? They will regain access to their account.`}
              {showConfirmDialog === 'reset' && `Are you sure you want to reset the password for ${selectedUser.name}? They will receive an email with instructions.`}
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowConfirmDialog(null)}
                className="px-4 py-2 text-sm bg-muted text-foreground rounded-md hover:bg-muted/80 transition-colors"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (showConfirmDialog === 'suspend') handleSuspendUser()
                  else if (showConfirmDialog === 'unsuspend') handleUnsuspendUser()
                  else if (showConfirmDialog === 'reset') handleResetPassword()
                }}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                  showConfirmDialog === 'suspend' 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                }`}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserDetails